import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Building2,
  Users,
  Video,
  Coins,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Check,
  RotateCcw,
  MapPin,
  Cpu,
  Handshake,
  Zap,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import HexMark from "@/components/motion/HexMark";

// ── Question deck ────────────────────────────────────────────────
const QUESTIONS = [
  {
    key: "event_type",
    label: "Event type",
    kicker: "01",
    prompt: "What are you planning?",
    hint: "Pick the closest match — we'll refine on the call.",
    options: [
      { v: "Corporate Meeting", icon: Building2, tone: "cyan" },
      { v: "Association / Conference", icon: Users, tone: "blue" },
      { v: "Gala or Fundraiser", icon: Sparkles, tone: "amber" },
      { v: "VIP / Film / Tour", icon: Video, tone: "rose" },
      { v: "Wedding or Special Event", icon: Handshake, tone: "cyan" },
      { v: "Other / Not sure", icon: MapPin, tone: "blue" },
    ],
  },
  {
    key: "attendees",
    label: "Attendees",
    kicker: "02",
    prompt: "How many guests?",
    hint: "Approximate is perfect.",
    options: [
      { v: "Under 100", icon: Users, tone: "cyan" },
      { v: "100 – 500", icon: Users, tone: "cyan" },
      { v: "500 – 1,500", icon: Users, tone: "blue" },
      { v: "1,500+", icon: Users, tone: "amber" },
    ],
  },
  {
    key: "format",
    label: "Format",
    kicker: "03",
    prompt: "In-person, hybrid, or virtual?",
    hint: "This shapes the tech stack we bring.",
    options: [
      { v: "In-Person", icon: Building2, tone: "cyan" },
      { v: "Hybrid", icon: Cpu, tone: "blue" },
      { v: "Virtual", icon: Video, tone: "amber" },
      { v: "Not sure yet", icon: MapPin, tone: "rose" },
    ],
  },
  {
    key: "budget",
    label: "Budget",
    kicker: "04",
    prompt: "Ballpark investment?",
    hint: "Range is fine — nothing binding.",
    options: [
      { v: "Under $50k", icon: Coins, tone: "cyan" },
      { v: "$50k – $250k", icon: Coins, tone: "blue" },
      { v: "$250k – $1M", icon: Coins, tone: "amber" },
      { v: "$1M+", icon: Coins, tone: "rose" },
    ],
  },
];

// ── Recommendation engine ─────────────────────────────────────────
function recommend(answers) {
  const { event_type = "", attendees = "", format = "", budget = "" } = answers;
  const big = /1,500|500 – 1,500|\$1M|\$250k/.test(attendees + budget);
  const hybridLike = /Hybrid|Virtual/.test(format);
  const vip = /VIP|Film/.test(event_type);
  const site = /Wedding/.test(event_type);

  if (vip) {
    return {
      chapter: "VIP & Film",
      chapterId: "vip-film",
      href: "/services/vip-film",
      icon: Video,
      title: "VIP & Film · Destination Management",
      pitch:
        "Ground logistics, private aviation, and white-glove hospitality for tours, productions, and executive travel.",
      cta: "See VIP & Film",
    };
  }
  if (site) {
    return {
      chapter: "Site Selection",
      chapterId: "site-selection",
      href: "/services/site-selection",
      icon: MapPin,
      title: "Site Selection & Sourcing",
      pitch:
        "Hand-picked venues, negotiated hotel blocks, and destination logistics — at no cost to you.",
      cta: "See Site Selection",
    };
  }
  if (hybridLike) {
    return {
      chapter: "Chapter B",
      chapterId: "chapter-02",
      href: "/services/event-tech",
      icon: Cpu,
      title: "Event Tech Concierge · One Team",
      pitch:
        "Registration, mobile, hybrid broadcast, and analytics — architected so your guests feel connected and engaged.",
      cta: "Explore Event Tech",
    };
  }
  if (big) {
    return {
      chapter: "Chapter A",
      chapterId: "chapter-01",
      href: "/services#chapter-01",
      icon: Zap,
      title: "Full-Service Strategic Meeting",
      pitch:
        "Certified project management, on-site directors, and end-to-end concierge — engineered for scale.",
      cta: "See Chapter A",
    };
  }
  return {
    chapter: "Concierge · Hourly",
    chapterId: "hourly",
    href: "/services",
    icon: Handshake,
    title: "Concierge · Hourly Retainer",
    pitch:
      "Retain us for a single scope, or scale up when you need it. Quiet, obsessive, accountable.",
    cta: "Explore Services",
  };
}

// ── Tone palettes ─────────────────────────────────────────────────
const TONE_MAP = {
  cyan: "from-cyan-500 to-sky-600 shadow-cyan-500/30",
  blue: "from-sky-500 to-blue-700 shadow-blue-500/30",
  amber: "from-amber-400 to-orange-500 shadow-amber-500/30",
  rose: "from-rose-400 to-pink-600 shadow-rose-500/30",
};

// ── Playful mid-quiz cheer ────────────────────────────────────────
const CHEERS = ["Nice pick!", "Great — noted.", "Almost there!", "Locked in ✨"];

export default function EventSimulator() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [cheerVisible, setCheerVisible] = useState(false);
  const total = QUESTIONS.length;
  const done = step >= total;

  const q = QUESTIONS[step];
  const progress = Math.round((step / total) * 100);

  const pick = (val) => {
    setAnswers((a) => ({ ...a, [q.key]: val }));
    setCheerVisible(true);
    setTimeout(() => setCheerVisible(false), 900);
    setTimeout(() => setStep((s) => s + 1), 450);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const restart = () => {
    setStep(0);
    setAnswers({});
  };

  // Store answers before jumping to Contact so the wizard can prefill
  const goToQuote = () => {
    try {
      const payload = { ...answers };
      // Attach derived recommendation so the admin sees a fit signal
      const r = recommend(answers);
      if (r) {
        payload.chapter = r.chapter;
        payload.chapter_id = r.chapterId;
      }
      sessionStorage.setItem("magee_quiz_answers", JSON.stringify(payload));
    } catch (e) {
      // sessionStorage may be blocked — the /contact page will still work manually
    }
  };

  const rec = done ? recommend(answers) : null;

  return (
    <section
      id="fit-finder"
      aria-label="Find your fit — event simulator"
      className="relative py-20 md:py-28 overflow-hidden"
      data-testid="event-simulator"
    >
      <div className="absolute inset-0 hex-pattern-dense opacity-40 pointer-events-none" />
      <div className="brand-bloom brand-bloom-cyan w-[520px] h-[520px] -top-32 -right-24 opacity-35" />
      <div className="brand-bloom brand-bloom-warm w-[420px] h-[420px] -bottom-32 -left-24 opacity-35" />
      <HexMark
        size={220}
        opacity={0.06}
        className="absolute left-6 top-16 rotate-[-14deg] pointer-events-none hidden lg:block"
      />
      <span className="corner-cross" style={{ top: 40, right: 40 }} />
      <span className="corner-cross" style={{ bottom: 40, left: 40 }} />

      <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 md:px-10">
        {/* Section header */}
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
            <div>
              <span className="chip">
                <Sparkles size={12} /> Find your fit &middot; 30 seconds
              </span>
              <h2 className="mt-5 md:mt-6 font-editorial text-3xl sm:text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl">
                Four questions.{" "}
                <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                  One perfect fit.
                </span>
              </h2>
            </div>
            <p className="max-w-sm text-[var(--text-muted)]">
              Answer four quick questions and we&rsquo;ll match you with the right
              service — then hand you off to a scoped quote.
            </p>
          </div>
        </Reveal>

        {/* Card */}
        <Reveal delay={0.15}>
          <div className="relative bg-white rounded-3xl border border-[var(--border)] shadow-[0_30px_80px_-40px_rgba(11,37,69,0.35)] overflow-hidden">
            {/* Progress rail */}
            <div className="h-1 bg-[var(--surface)]">
              <motion.div
                className="h-full bg-gradient-to-r from-cyan-400 to-blue-600"
                initial={{ width: 0 }}
                animate={{ width: `${done ? 100 : progress}%` }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>

            <div className="relative p-6 md:p-10 min-h-[520px]">
              {/* Cheer bubble */}
              <AnimatePresence>
                {cheerVisible && !done && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.95 }}
                    className="absolute top-4 right-6 z-10 font-mono-brand text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-700"
                  >
                    {CHEERS[step]} ✨
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence mode="wait">
                {!done ? (
                  <motion.div
                    key={`q-${step}`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.35 }}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text)]/50">
                        Question {q.kicker} / 04 · {q.label}
                      </span>
                      <span className="h-px flex-1 bg-[var(--border)]" />
                    </div>
                    <h3 className="font-editorial text-2xl sm:text-3xl md:text-4xl leading-tight tracking-tight mb-2">
                      {q.prompt}
                    </h3>
                    <p className="text-[var(--text-muted)] text-sm mb-8">
                      {q.hint}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                      {q.options.map((opt, i) => (
                        <motion.button
                          key={opt.v}
                          type="button"
                          onClick={() => pick(opt.v)}
                          data-testid={`quiz-option-${q.key}-${i}`}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.04 * i, duration: 0.35 }}
                          whileHover={{ y: -4 }}
                          whileTap={{ scale: 0.97 }}
                          className="group relative bg-white border border-[var(--border)] rounded-2xl p-5 text-left overflow-hidden hover:border-[var(--accent)]/50 hover:shadow-[0_18px_40px_-20px_rgba(6,182,212,0.4)] transition-all"
                        >
                          <div
                            className={`w-10 h-10 rounded-xl bg-gradient-to-br ${TONE_MAP[opt.tone]} text-white flex items-center justify-center shadow-lg mb-4`}
                          >
                            <opt.icon size={18} />
                          </div>
                          <div className="font-editorial text-[15px] leading-tight tracking-tight text-[var(--text)]">
                            {opt.v}
                          </div>
                          <ArrowRight
                            size={14}
                            className="absolute bottom-4 right-4 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"
                          />
                        </motion.button>
                      ))}
                    </div>

                    <div className="mt-8 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={back}
                        disabled={step === 0}
                        data-testid="quiz-back"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-mono-brand text-[var(--text)]/60 hover:text-[var(--text)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <ArrowLeft size={12} /> Back
                      </button>
                      <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text)]/40">
                        {step + 1} of {total}
                      </span>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="relative"
                    data-testid="quiz-result"
                  >
                    {/* Confetti sparkles */}
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                      {[...Array(14)].map((_, i) => (
                        <motion.span
                          key={i}
                          initial={{
                            opacity: 0,
                            x: "50%",
                            y: "40%",
                            scale: 0,
                          }}
                          animate={{
                            opacity: [0, 1, 0],
                            x: `${Math.random() * 100}%`,
                            y: `${Math.random() * 90}%`,
                            scale: [0, 1, 0.6],
                          }}
                          transition={{
                            duration: 1.6,
                            delay: 0.1 + i * 0.05,
                            ease: [0.16, 1, 0.3, 1],
                          }}
                          className={`absolute w-2 h-2 rounded-full ${
                            i % 3 === 0
                              ? "bg-cyan-400"
                              : i % 3 === 1
                                ? "bg-amber-400"
                                : "bg-blue-500"
                          }`}
                        />
                      ))}
                    </div>

                    <div className="flex items-center gap-3 mb-6">
                      <Check size={16} className="text-[var(--accent)]" />
                      <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text)]/50">
                        Match found &middot; Recommendation ready
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-center">
                      <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-5">
                        <div
                          className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br ${TONE_MAP.cyan} text-white flex items-center justify-center shadow-xl`}
                        >
                          <rec.icon size={32} />
                        </div>
                        <div>
                          <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-600">
                            You look like a
                          </div>
                          <div className="mt-1 font-editorial text-2xl md:text-3xl leading-tight tracking-tight text-[var(--text)]">
                            {rec.chapter}
                            <span className="text-[var(--text-muted)]"> · fit</span>
                          </div>
                        </div>
                      </div>

                      <div className="md:col-span-3 md:pl-6 md:border-l md:border-[var(--border)]">
                        <h3 className="font-editorial text-xl md:text-2xl tracking-tight leading-snug mb-3">
                          {rec.title}
                        </h3>
                        <p className="text-[var(--text-muted)] leading-relaxed mb-5">
                          {rec.pitch}
                        </p>

                        {/* Answer summary */}
                        <div className="grid grid-cols-2 gap-2 mb-6">
                          {QUESTIONS.map((qq) => (
                            <div
                              key={qq.key}
                              className="flex items-baseline gap-2 text-[12px]"
                            >
                              <span className="font-mono-brand text-[9px] uppercase tracking-[0.22em] text-[var(--text)]/40">
                                {qq.label}
                              </span>
                              <span className="text-[var(--text)]/80 truncate">
                                {answers[qq.key]}
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="flex flex-wrap gap-3">
                          <Link
                            to="/contact"
                            onClick={goToQuote}
                            data-testid="quiz-cta-quote"
                            className="btn-primary"
                          >
                            Get my quote <ArrowRight size={14} />
                          </Link>
                          <Link
                            to={rec.href}
                            data-testid="quiz-cta-learn"
                            className="btn-ghost"
                          >
                            {rec.cta} <ArrowRight size={14} />
                          </Link>
                          <button
                            type="button"
                            onClick={restart}
                            data-testid="quiz-restart"
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-mono-brand text-[var(--text)]/50 hover:text-[var(--text)] transition-colors"
                          >
                            <RotateCcw size={12} /> Start over
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
